import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabase';
import ProtocolReport from './ProtocolReport';
import JellyfishLoader from './JellyfishLoader';
import './InlineProtocolReport.css';

// One request per snapshot id: cache resolved result + dedupe in-flight.
const RESULT_TTL_MS = 60_000;
const resultCache = new Map(); // id -> { data, error, at }
const inFlight = new Map();   // id -> Promise<{ data, error }>

function fetchSnapshotById(snapshotId) {
  const cached = resultCache.get(snapshotId);
  if (cached && Date.now() - cached.at < RESULT_TTL_MS) {
    return cached.error ? Promise.reject(cached.error) : Promise.resolve({ data: cached.data, error: null });
  }
  if (inFlight.has(snapshotId)) {
    return inFlight.get(snapshotId);
  }
  const promise = supabase
    .from('protocol_report_snapshots')
    .select('*')
    .eq('id', snapshotId)
    .single()
    .then((res) => {
      inFlight.delete(snapshotId);
      resultCache.set(snapshotId, { data: res.data, error: res.error, at: Date.now() });
      if (res.error) throw res.error;
      return res;
    })
    .catch((err) => {
      inFlight.delete(snapshotId);
      resultCache.set(snapshotId, { data: null, error: err, at: Date.now() });
      throw err;
    });
  inFlight.set(snapshotId, promise);
  return promise;
}

/**
 * Inline Protocol Report Component
 * Displays a protocol report inline in a post with page navigation
 * styled to match the insights page modal, but embedded directly in the post card
 */
const InlineProtocolReport = ({ snapshotId, protocolId, onPageChange, controlsPortalId }) => {
  const [snapshotData, setSnapshotData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const wrapperRef = useRef(null);
  const lastPageInfoRef = useRef({ currentPage: 1, totalPages: 1 });

  useEffect(() => {
    if (!snapshotId) return;

    let isMounted = true;

    const loadSnapshot = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          if (isMounted) {
            setError('User not authenticated');
            setLoading(false);
          }
          return;
        }

        const { data } = await fetchSnapshotById(snapshotId);
        if (!isMounted) return;
        setSnapshotData(data);
      } catch (err) {
        if (isMounted) {
          console.error('Error loading protocol report snapshot:', err);
          setError('Failed to load protocol report');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadSnapshot();
    return () => { isMounted = false; };
  }, [snapshotId]);

  // Use a ref to store the callback to avoid dependency issues
  const onPageChangeRef = useRef(onPageChange);
  useEffect(() => {
    onPageChangeRef.current = onPageChange;
  }, [onPageChange]);

  // Reset tracking when snapshotData loads
  useEffect(() => {
    if (snapshotData) {
      lastPageInfoRef.current = { currentPage: null, totalPages: null };
    }
  }, [snapshotData]); // Only reset when snapshotData changes

  // Watch for page changes and update page numbers
  useEffect(() => {
    if (!wrapperRef.current || !snapshotData) return;

    const updatePageInfo = () => {
      if (!wrapperRef.current) return;
      const paginationElement = wrapperRef.current.querySelector('.report-pagination');
      if (paginationElement) {
        const text = paginationElement.textContent.trim();
        const match = text.match(/(\d+)\/(\d+)/);
        if (match) {
          const newCurrentPage = parseInt(match[1], 10);
          const newTotalPages = parseInt(match[2], 10);
          
          const lastPageInfo = lastPageInfoRef.current;
          const hasChanged = lastPageInfo.currentPage === null || 
                            lastPageInfo.totalPages === null ||
                            lastPageInfo.currentPage !== newCurrentPage || 
                            lastPageInfo.totalPages !== newTotalPages;
          
          // Only update state if values actually changed to prevent infinite loops
          if (hasChanged) {
            if (lastPageInfo.currentPage !== newCurrentPage) {
              setCurrentPage(newCurrentPage);
            }
            if (lastPageInfo.totalPages !== newTotalPages) {
              setTotalPages(newTotalPages);
            }
            
            // Update ref
            lastPageInfoRef.current = { currentPage: newCurrentPage, totalPages: newTotalPages };
            
            // Call callback using ref to avoid dependency on callback itself
            if (onPageChangeRef.current) {
              onPageChangeRef.current(newCurrentPage, newTotalPages);
            }
          }
        }
      }
    };

    // Initial update
    updatePageInfo();

    // Watch for changes using MutationObserver
    const observer = new MutationObserver(() => {
      updatePageInfo();
    });

    if (wrapperRef.current) {
      const paginationElement = wrapperRef.current.querySelector('.report-pagination');
      if (paginationElement) {
        observer.observe(paginationElement, {
          childList: true,
          characterData: true,
          subtree: true
        });
      }
    }

    // Also check periodically in case MutationObserver misses updates
    const interval = setInterval(updatePageInfo, 100);

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, [snapshotData]); // Removed onPageChange from dependencies since we use a ref

  if (loading) {
    return (
      <div className="inline-protocol-report inline-protocol-report--loading">
        <div className="inline-protocol-report-loading">
          <JellyfishLoader />
        </div>
      </div>
    );
  }

  if (error || !snapshotData) {
    return (
      <div className="inline-protocol-report inline-protocol-report--error">
        <div className="inline-protocol-report-error">{error || 'Report not found'}</div>
      </div>
    );
  }

  // Use ProtocolReport component in inline/modal mode
  // This will display the report with the same look as the insights page modal
  // but embedded directly in the post card with turnable pages
  return (
    <div className="inline-protocol-report">
      <div className="inline-protocol-report-wrapper" ref={wrapperRef}>
        <ProtocolReport 
          reportId={snapshotId}
          protocolId={protocolId || snapshotData.protocol_id}
          isModal={true}
          controlsPortalId={controlsPortalId}
        />
      </div>
    </div>
  );
};

export default InlineProtocolReport;

